import { computed } from 'vue'
import type { AppointmentsState } from './state'

export const createGetters = (state: AppointmentsState) => {
  const filteredAppointments = computed(() => {
    let result = [...state.appointments]

    // Filter by search
    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(search) ||
          a.description?.toLowerCase().includes(search)
      )
    }

    // Filter by status
    if (state.filters.status) {
      result = result.filter((a) => a.status === state.filters.status)
    }

    // Filter by type
    if (state.filters.type) {
      result = result.filter((a) => a.type === state.filters.type)
    }

    // Filter by organization
    if (state.filters.organizationId) {
      result = result.filter((a) => a.organizationId === state.filters.organizationId)
    }

    return result
  })

  const requestedAppointments = computed(() =>
    state.appointments.filter((a) => a.status === 'requested')
  )

  const proposedAppointments = computed(() =>
    state.appointments.filter((a) => a.status === 'proposed')
  )

  const confirmedAppointments = computed(() =>
    state.appointments.filter((a) => a.status === 'confirmed')
  )

  const completedAppointments = computed(() =>
    state.appointments.filter((a) => a.status === 'completed')
  )

  const cancelledAppointments = computed(() =>
    state.appointments.filter((a) => a.status === 'cancelled')
  )

  const upcomingAppointments = computed(() => {
    const now = new Date()
    return state.appointments
      .filter((a) => {
        const dateToCheck = a.confirmedDateTime || a.requestedDateTime
        return (a.status === 'confirmed' || a.status === 'proposed') && dateToCheck > now
      })
      .sort((a, b) => {
        const dateA = a.confirmedDateTime || a.requestedDateTime
        const dateB = b.confirmedDateTime || b.requestedDateTime
        return dateA.getTime() - dateB.getTime()
      })
  })

  const todayAppointments = computed(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    return state.appointments.filter((a) => {
      const dateToCheck = a.confirmedDateTime || a.requestedDateTime
      return dateToCheck >= today && dateToCheck < tomorrow
    })
  })

  const appointmentsByType = computed(() => {
    const grouped: Record<string, typeof state.appointments> = {}
    for (const appointment of state.appointments) {
      if (!grouped[appointment.type]) {
        grouped[appointment.type] = []
      }
      grouped[appointment.type].push(appointment)
    }
    return grouped
  })

  return {
    filteredAppointments,
    requestedAppointments,
    proposedAppointments,
    confirmedAppointments,
    completedAppointments,
    cancelledAppointments,
    upcomingAppointments,
    todayAppointments,
    appointmentsByType,
  }
}
